import { forwardRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, ButtonGroup, Col, Dropdown, FormLabel, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import "./SubscriberList.css";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import Common from "../../../../constants/Common";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import { formatDate, formatDateTime, formatDateToYYYYMMDD, parseYYYMMDDToDate } from "../../../../utils/DateUtils";
import ModalPageEvent from "../../../components/Common/Modal/ModalPageEvent";
import YesNoSelect from "../../../components/Common/YesNoSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import InputText from "../../../components/Forms/Element/InputText";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import CancelIcon from "../../../components/Icon/CancelIcon";
import StarsIcon from "../../../components/Icon/StarsIcon";
import GridCustomBase from "../../../components/GridCustom/GridCustomBase";
import PaymentLinkActions from "../../../components/Custom/Charge/PaymentLinkActions";
import OutsidePaymentAction from "../../../components/Custom/Charge/OutsidePaymentAction";
import CopyButton from "../../../components/Common/CopyButton";
import PageTitle from "../../../layouts/PageTitle";
import { ApiError } from "../../../components/Common/ApiError";
import { errorAlert, successAlert, warnAlert, warnAlertYesOrNo } from "../../../components/alert/defaultAlert";
import { getSubscriptionSituation } from "../../../components/Custom/Subscription/subscriptionSituation";
import PlanAttentionNotices from "../../../components/Custom/Subscription/Plan/PlanAttentionNotices";
import StorePackageRenewModal from "../../../components/Custom/Subscription/StorePackageRenewModal";


const SubDetailSection = ({ icon, title, subtitle, className = "", collapsible = false, defaultOpen = false, children }) => {
    const [expanded, setExpanded] = useState(defaultOpen);

    return (
    <section className={`sub-detail__section ${className} ${collapsible ? "sub-detail__section--collapsible" : ""} ${expanded ? "is-expanded" : ""}`.trim()}>
        <div className="sub-detail__section-header">
            <div className="sub-detail__section-heading">
                <span className="sub-detail__section-icon"><i className={icon}></i></span>
                <div>
                    <h6>{title}</h6>
                    {subtitle && <p>{subtitle}</p>}
                </div>
            </div>
            {collapsible && (
                <button
                    type="button"
                    className="sub-detail__section-toggle"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                    aria-label={`${expanded ? "Recolher" : "Expandir"} ${title}`}
                >
                    <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
                </button>
            )}
        </div>
        <div className="sub-detail__section-content" hidden={collapsible && !expanded}>{children}</div>
    </section>
    );
};

// Renderiza o menu do dropdown por linha direto no <body>, via portal.
// Necessário porque a tabela tem overflow:auto (corta o menu) e o layout tem
// ancestrais com transform (o que quebra o posicionamento com
// popperConfig={{ strategy: "fixed" }}, fazendo o menu "pular" para o topo da
// página). Fora da árvore de ambos os ancestrais, o Popper posiciona certo -
// o contexto do Dropdown chega normalmente via React Context, portais não o
// interrompem.
const RowActionsMenu = forwardRef(({ children, style, className, "aria-labelledby": labeledBy }, ref) => {
    return ReactDOM.createPortal(
        <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
            {children}
        </div>,
        document.body
    );
});

const SubDetailItem = ({ label, value, accent = false }) => (
    <div className="sub-detail__item">
        <span className="sub-detail__item-label">{label}</span>
        <strong className={`sub-detail__item-value${accent ? " sub-detail__item-value--accent" : ""}`}>
            {value !== undefined && value !== null && value !== "" ? value : "-"}
        </strong>
    </div>
);

const isLegacyEfiSubscription = (subscription) => (
    subscription?.paymentProvider?.toUpperCase() === "EFI"
    // Algumas assinaturas importadas antes do campo paymentProvider existir têm apenas
    // o identificador numérico antigo; o backend valida este mesmo caso antes de aceitar.
    || (!subscription?.paymentProvider && /^\d+$/.test(String(subscription?.integrationId || "")))
);

/**
 * Filtros iniciais vindos da URL. O destaque do mês abre a lista em
 * ?withoutSeller=1&startDate=yyyy-MM-dd&endDate=yyyy-MM-dd para mostrar só os contratos do
 * período que ficaram sem vendedor e, por isso, não pontuaram.
 */
const readInitialUrlFilters = (search) => {
    const params = new URLSearchParams(search || "");
    const parseDate = (value) => {
        try {
            return value ? parseYYYMMDDToDate(value) : null;
        } catch {
            // Data ilegível na URL não pode quebrar a tela: a lista abre sem o período.
            return null;
        }
    };
    const startDate = parseDate(params.get("startDate"));
    const endDate = parseDate(params.get("endDate"));
    return {
        // O período só vale com as duas pontas, a mesma exigência da busca manual.
        startDate: startDate && endDate ? startDate : null,
        endDate: startDate && endDate ? endDate : null,
        withoutSeller: params.get("withoutSeller") === "1"
    };
};

const SubscriberList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [initialUrlFilters] = useState(() => readInitialUrlFilters(location.search));
    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [cancelLegacyEfiLocally, setCancelLegacyEfiLocally] = useState(false);
    // O cancelamento encerra a cobrança recorrente no provedor e não tem desfazer pelo
    // Colmeia: o operador precisa marcar o aceite antes de o botão liberar.
    const [cancellationAcknowledged, setCancellationAcknowledged] = useState(false);
    // Até quando o assinante já pagou, quando o ciclo quitado ainda está correndo. O
    // backend recusa o cancelamento sem confirmação nesse caso, e aqui é onde o operador
    // vê a data antes de decidir.
    const [paidCycleUntil, setPaidCycleUntil] = useState(null);
    const [showLostCustomersModal, setShowLostCustomersModal] = useState(false);
    const [lostCustomers, setLostCustomers] = useState([]);
    const [loadingLostCustomers, setLoadingLostCustomers] = useState(false);


    // Buscado só ao abrir a lista: é uma varredura da carteira inteira e não precisa
    // acontecer a cada carregamento da tela.
    const openLostCustomers = () => {
        setShowLostCustomersModal(true);
        setLoadingLostCustomers(true);
        axiosInstance.get(`${Api.subscription.subscription}/lost-customers`)
            .then(({ data }) => setLostCustomers(Array.isArray(data) ? data : []))
            .catch((error) => {
                setLostCustomers([]);
                ApiError(error);
            })
            .finally(() => setLoadingLostCustomers(false));
    };
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    // Pacote da loja sendo renovado; preenchido abre o modal de renovação.
    const [renewingSubscription, setRenewingSubscription] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    // A listagem abre sem recorte para mostrar toda a carteira. O período só é
    // aplicado quando o usuário escolher explicitamente as duas datas.
    const [startDate, setStartDate] = useState(initialUrlFilters.startDate);
    const [endDate, setEndDate] = useState(initialUrlFilters.endDate);
    const [customerName, setCustomerName] = useState("");
    const [planName, setPlanName] = useState("");
    const [employeeName, setEmployeeName] = useState("");
    // Sim/Não sobre ter vendedor registrado. "Não" é o recorte que importa: contrato vindo da
    // sincronização do provedor chega sem vendedor e não pontua no destaque do mês.
    const [sellerInformed, setSellerInformed] = useState(
        initialUrlFilters.withoutSeller ? { value: "N", label: t("no") } : null);
    const [active, setActive] = useState(null);
    const [hasOverdueCharge, setHasOverdueCharge] = useState(null);
    const [planMovementType, setPlanMovementType] = useState(null);
    const [pendingOnly, setPendingOnly] = useState(false);
    const [dateFilterField, setDateFilterField] = useState("createdAt");
    const [appliedSummaryCard, setAppliedSummaryCard] = useState(null);
    const [pageable, setPageable] = useState({});
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    // Começa carregando para os cards nunca exibirem "0"/área vazia antes da
    // primeira resposta da API.
    const [showLoading, setShowLoading] = useState(true);
    const [summaryData, setSummaryData] = useState(null);
    const [planTransitions, setPlanTransitions] = useState([]);
    const [loadingPlanTransitions, setLoadingPlanTransitions] = useState(false);
    const [recentCharges, setRecentCharges] = useState([]);
    const [loadingRecentCharges, setLoadingRecentCharges] = useState(false);

    const selectedSubscription = selectedRows[0] || null;

    // Edição do vendedor no detalhe. Contrato vindo da sincronização chega sem vendedor e não
    // pontua no destaque do mês; o vendedor só existia no formulário de criação.
    const [editingSeller, setEditingSeller] = useState(false);
    const [sellerDraft, setSellerDraft] = useState(null);
    const [savingSeller, setSavingSeller] = useState(false);

    // Trocar de assinatura fecha a edição, para o rascunho de uma não ser salvo na outra.
    useEffect(() => {
        setEditingSeller(false);
    }, [selectedSubscription?.id]);

    // Abre a edição já com o vendedor atual, quando houver, para corrigir em vez de redigitar.
    const startSellerEdit = () => {
        const current = selectedSubscription?.employee;
        setSellerDraft(current?.id ? { value: current.id, label: current.fullName } : null);
        setEditingSeller(true);
    };

    // Grava só o vendedor no Colmeia; o contrato no provedor não muda.
    const saveSeller = () => {
        if (!selectedSubscription) return;
        setSavingSeller(true);
        axiosInstance.patch(`${Api.subscription.subscription}/${selectedSubscription.id}/employee`, {
            employeeId: sellerDraft?.value ?? null
        })
            .then(({ data }) => {
                // Atualiza o detalhe e a linha da grade sem recarregar a lista: com o filtro
                // "Vendedor informado: Não" a linha sairia da tela antes de o operador conferir.
                const employee = data?.employee ?? null;
                const withSeller = (row) => (row.id === selectedSubscription.id ? { ...row, employee } : row);
                setSelectedRows((current) => current.map(withSeller));
                setRows((current) => current.map(withSeller));
                setEditingSeller(false);
            })
            .catch((error) => ApiError(error))
            .finally(() => setSavingSeller(false));
    };

    const planMovementOptions = [
        { value: "UPGRADE", label: "↑ Upgrade" },
        { value: "DOWNGRADE", label: "↓ Downgrade" },
        { value: "PLAN_CHANGE", label: "↔ Troca de plano" }
    ];

    const getProviderBillingTypeLabel = (providerBillingType) => {
        switch (providerBillingType) {
            case "CREDIT_CARD":
                return t("credit_card");
            case "DEBIT_CARD":
                return t("debit_card");
            case "PIX":
                return t("pix");
            case "BOLETO":
                return t("banking_billet");
            default:
                return "-";
        }
    };

    // Monta os filtros informados na tela para a pesquisa paginada de assinaturas.
    const buildFilters = (overrides = {}) => {
        const criteria = {
            startDate,
            endDate,
            customerName,
            planName,
            employeeName,
            sellerInformed,
            active,
            hasOverdueCharge,
            planMovementType,
            pendingOnly,
            dateFilterField,
            ...overrides
        };
        const filters = [];

        if ((criteria.startDate && !criteria.endDate) || (!criteria.startDate && criteria.endDate)) {
            warnAlert(t("period_filter_requires_both_dates"));
            return null;
        }

        if (criteria.startDate && criteria.endDate) {
            filters.push({
                field: criteria.dateFilterField,
                value: `${formatDateToYYYYMMDD(criteria.startDate)}T00:00:00`,
                operation: SearchOperation.GREATER_THAN_EQUAL
            });
            filters.push({
                field: criteria.dateFilterField,
                value: `${formatDateToYYYYMMDD(criteria.endDate)}T23:59:59`,
                operation: SearchOperation.LESS_THAN_EQUAL
            });
        }

        if (criteria.dateFilterField === "cancellationDate") {
            // Ao abrir cancelamentos, a lista e o indicador usam a data em que o
            // contrato foi encerrado, não a data em que foi criado.
            filters.push({
                field: "cancellationDate",
                operation: SearchOperation.IS_NOT_NULL
            });
            // Contrato desfeito sem nenhuma mensalidade paga (link refeito, cartão
            // recusado) não conta como cancelamento no card; a lista tira os mesmos.
            filters.push({
                field: "unpaidCancellation",
                value: "N",
                operation: SearchOperation.EQUAL
            });
        }

        if (criteria.customerName) {
            filters.push({
                field: "customer.fullName",
                value: criteria.customerName,
                // Busca por palavra, igual ao cadastro de clientes: o nome completo costuma
                // ter nome do meio, e com MATCH o operador digitava "nome sobrenome" e nao
                // achava ninguem, porque o LIKE unico exige os dois grudados.
                operation: SearchOperation.MATCH_WORDS
            });
        }

        if (criteria.planName) {
            filters.push({
                field: "plan.name",
                value: criteria.planName,
                operation: SearchOperation.MATCH
            });
        }

        if (criteria.employeeName) {
            filters.push({
                field: "employee.fullName",
                value: criteria.employeeName,
                operation: SearchOperation.MATCH
            });
        }

        if (criteria.sellerInformed) {
            filters.push({
                field: "employee",
                operation: criteria.sellerInformed.value === "N" ? SearchOperation.IS_NULL : SearchOperation.IS_NOT_NULL
            });
        }

        if (criteria.active) {
            if (criteria.active.value === "Y") {
                filters.push({
                    field: "cancellationDate",
                    operation: SearchOperation.IS_NULL
                });
                filters.push({
                    field: "status",
                    value: "ACTIVE",
                    operation: SearchOperation.EQUAL
                });
            } else if (criteria.active.value === "N") {
                filters.push({
                    field: "cancellationDate",
                    operation: SearchOperation.IS_NOT_NULL
                });
                filters.push({
                    field: "status",
                    value: "INACTIVE",
                    operation: SearchOperation.EQUAL
                });
            }
        }

        if (criteria.hasOverdueCharge) {
            filters.push({
                field: "hasOverdueCharge",
                value: criteria.hasOverdueCharge.value,
                operation: SearchOperation.EQUAL
            });
        }

        if (criteria.planMovementType) {
            filters.push({
                field: "planMovementType",
                value: criteria.planMovementType.value,
                operation: SearchOperation.EQUAL
            });
        }

        if (criteria.pendingOnly) {
            filters.push({
                field: "pendingFirstPayment",
                value: true,
                operation: SearchOperation.EQUAL
            });
        }

        return filters;
    };

    // Executa a busca da listagem aplicando filtros, paginação e ordenação padrão.
    const executeSearch = (targetPage = page, targetSize = size, filterOverrides = {}) => {
        const filters = buildFilters(filterOverrides);
        if (filters === null) {
            return;
        }

        const payload = {
            filters,
            page: targetPage,
            size: targetSize,
            orders: [{
                field: "id",
                direction: "DESC"
            }]
        };

        setShowLoading(true);

        // O backend mantém os três indicadores atuais globais e usa somente o período
        // para calcular clientes efetivamente cancelados.
        const summaryPayload = { filters };

        Promise.all([
            axiosInstance.post(`${Api.subscription.subscription}/pageable`, payload),
            axiosInstance.post(`${Api.subscription.subscription}/summary`, summaryPayload)
        ])
            .then(([pageableResponse, summaryResponse]) => {
                setPageable(pageableResponse.data);
                setRows(pageableResponse.data.content || []);
                setSelectedRows([]);
                setSummaryData(summaryResponse.data);
            })
            .catch((error) => {
                errorAlert(error);
            })
            .finally(() => {
                setShowLoading(false);
            });
    };

    const scrollToSubscriberResults = () => {
        window.requestAnimationFrame(() => {
            const results = document.getElementById("subscriber-list-results");
            if (!results) return;

            const reduceMotion = window.matchMedia
                && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            results.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
        });
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        executeSearch(page, size);
    }, [page, size]);
    /* eslint-enable react-hooks/exhaustive-deps */

    // Dispara a pesquisa reiniciando a paginação quando necessário.
    const handleSearch = () => {
        if (page !== 0) {
            setPage(0);
            return;
        }

        executeSearch(0, size);
    };

    // Limpa os filtros da tela e recarrega a listagem a partir da primeira página.
    const handleClearFilters = () => {
        const clearedFilters = {
            startDate: null,
            endDate: null,
            customerName: "",
            planName: "",
            employeeName: "",
            sellerInformed: null,
            active: null,
            hasOverdueCharge: null,
            planMovementType: null,
            pendingOnly: false,
            dateFilterField: "createdAt"
        };
        setStartDate(null);
        setEndDate(null);
        setCustomerName("");
        setPlanName("");
        setEmployeeName("");
        setSellerInformed(null);
        setActive(null);
        setHasOverdueCharge(null);
        setPlanMovementType(null);
        setPendingOnly(false);
        setDateFilterField("createdAt");
        setAppliedSummaryCard(null);
        setSelectedRows([]);

        if (page !== 0) {
            setPage(0);
            return;
        }

        executeSearch(0, size, clearedFilters);
    };

    // Cards que representam totais abrem a mesma tabela já recortada para aquele
    // grupo. Os filtros sem relação com o card são limpos para o resultado não
    // parecer menor que o número exibido.
    const applySummaryCardFilter = (cardKey) => {
        const keepCancellationPeriod = cardKey === "cancellationsInPeriod" && startDate && endDate;
        const nextFilters = {
            startDate: keepCancellationPeriod ? startDate : null,
            endDate: keepCancellationPeriod ? endDate : null,
            customerName: "",
            planName: "",
            employeeName: "",
            sellerInformed: null,
            active: null,
            hasOverdueCharge: null,
            planMovementType: null,
            pendingOnly: false,
            dateFilterField: cardKey === "cancellationsInPeriod" ? "cancellationDate" : "createdAt"
        };

        if (cardKey === "total") {
            nextFilters.active = { value: "Y", label: t("yes") };
        } else if (cardKey === "active") {
            nextFilters.active = { value: "Y", label: t("yes") };
            nextFilters.hasOverdueCharge = { value: "N", label: t("no") };
        } else if (cardKey === "overdue") {
            nextFilters.active = { value: "Y", label: t("yes") };
            nextFilters.hasOverdueCharge = { value: "Y", label: t("yes") };
        } else if (cardKey === "pending") {
            nextFilters.pendingOnly = true;
        } else if (cardKey !== "cancellationsInPeriod") {
            return;
        }

        setStartDate(nextFilters.startDate);
        setEndDate(nextFilters.endDate);
        setCustomerName("");
        setPlanName("");
        setEmployeeName("");
        setSellerInformed(null);
        setActive(nextFilters.active);
        setHasOverdueCharge(nextFilters.hasOverdueCharge);
        setPlanMovementType(null);
        setPendingOnly(nextFilters.pendingOnly);
        setDateFilterField(nextFilters.dateFilterField);
        setAppliedSummaryCard(cardKey);
        setSelectedRows([]);

        if (page !== 0) {
            setPage(0);
            scrollToSubscriberResults();
            return;
        }

        executeSearch(0, size, nextFilters);
        scrollToSubscriberResults();
    };

    // Mantém apenas uma linha selecionada por vez, alternando a seleção ao clicar novamente.
    const toggleRowSelection = (row) => {
        if (row?.cancellationDate) {
            return;
        }

        setSelectedRows((current) => current[0]?.id === row.id ? [] : [row]);
    };

    // Consulta o ciclo pago da assinatura ao abrir o cancelamento. Falha na consulta não
    // impede o operador de seguir: o backend continua barrando o cancelamento sem
    // confirmação, então o pior caso é ele ver o aviso como mensagem de erro em vez de no
    // modal.
    const loadPaidCycleUntil = (subscriptionId) => {
        setPaidCycleUntil(null);
        if (!subscriptionId) {
            return;
        }

        axiosInstance.get(`${Api.subscription.subscription}/${subscriptionId}/paid-cycle-end`)
            .then((response) => setPaidCycleUntil(response?.data?.paidUntil || null))
            .catch(() => setPaidCycleUntil(null));
    };

    // Abre o modal de cancelamento para a assinatura selecionada.
    const openCancelDialog = () => {
        if (!selectedSubscription) {
            warnAlert(t("validation.required.row"));
            return;
        }

        if (selectedSubscription.cancellationDate) {
            warnAlert(t("subscription_already_canceled"));
            return;
        }

        setCancellationAcknowledged(false);
        setCancelLegacyEfiLocally(false);
        loadPaidCycleUntil(selectedSubscription.id);
        setShowCancellationModal(true);
    };

    // O histórico é buscado apenas ao abrir o detalhe para não adicionar uma
    // requisição por linha na listagem de uma carteira grande.
    const loadPlanTransitions = (subscription) => {
        if (!subscription?.id) {
            setPlanTransitions([]);
            return;
        }
        setLoadingPlanTransitions(true);
        axiosInstance.get(`${Api.subscription.subscription}/${subscription.id}/plan-transitions`)
            .then(({ data }) => setPlanTransitions(Array.isArray(data) ? data : []))
            .catch(() => setPlanTransitions([]))
            .finally(() => setLoadingPlanTransitions(false));
    };

    const loadRecentCharges = (subscription) => {
        if (!subscription?.id) {
            setRecentCharges([]);
            return;
        }
        setLoadingRecentCharges(true);
        axiosInstance.get(`${Api.subscription.charge}/subscription/${subscription.id}/recent`)
            .then(({ data }) => setRecentCharges(Array.isArray(data) ? data : []))
            .catch(() => setRecentCharges([]))
            .finally(() => setLoadingRecentCharges(false));
    };

    // Guarda o link recém-gerado na linha correspondente, para os botões de copiar e
    // WhatsApp aparecerem sem precisar reabrir o detalhe.
    const updateRecentChargeLink = (chargeId, paymentLink, paymentMethod) => {
        setRecentCharges((charges) => charges.map((charge) => (
            charge.id === chargeId ? { ...charge, paymentLink, ...(paymentMethod ? { paymentMethod } : {}) } : charge
        )));
    };

    const loadSubscriptionDetailData = (subscription) => {
        loadPlanTransitions(subscription);
        loadRecentCharges(subscription);
    };

    // Abre o modal com os detalhes da assinatura selecionada.
    const openDetailsModal = () => {
        if (!selectedSubscription) {
            warnAlert(t("validation.required.row"));
            return;
        }
        loadSubscriptionDetailData(selectedSubscription);
        setShowDetailsModal(true);
    };

    // Abre os detalhes de uma linha específica a partir do menu de ações da tabela.
    const openDetailsModalForRow = (row) => {
        setSelectedRows([row]);
        loadSubscriptionDetailData(row);
        setShowDetailsModal(true);
    };

    // Abre o cancelamento de uma linha específica a partir do menu de ações da tabela.
    const openCancelDialogForRow = (row) => {
        if (row.cancellationDate) {
            warnAlert(t("subscription_already_canceled"));
            return;
        }

        setSelectedRows([row]);
        setCancellationAcknowledged(false);
        setCancelLegacyEfiLocally(false);
        loadPaidCycleUntil(row.id);
        setShowCancellationModal(true);
    };

    // Solicita o cancelamento da assinatura selecionada e atualiza a listagem após a confirmação.
    const handleCancel = () => {
        if (!selectedSubscription || !cancellationAcknowledged) {
            return;
        }

        if (selectedSubscription.cancellationDate) {
            warnAlert(t("subscription_already_canceled"));
            return;
        }

        // confirmPaidCycle acompanha o aceite do operador: o checkbox do modal é onde ele
        // declara ter lido o aviso, inclusive o do ciclo ainda pago.
        axiosInstance.patch(`${Api.subscription.subscription}/cancel`, {
            id: selectedSubscription.id,
            confirmPaidCycle: true,
            cancelLegacyEfiLocally
        })
            .then(() => {
                successAlert(t("message.transaction_successfully"), () => {
                    setShowCancellationModal(false);
                    setShowDetailsModal(false);
                    executeSearch(page, size);
                });
            })
            .catch((error) => ApiError(error));
    };

    // Traz de volta uma assinatura cancelada por engano. O backend só aceita quando o
    // contrato ainda existe no provedor de pagamento; quando não existe mais, ele recusa
    // com a explicação, e o ApiError já mostra essa mensagem ao operador.
    const handleReactivate = (subscription) => {
        if (!subscription) {
            warnAlert(t("validation.required.row"));
            return;
        }

        if (!subscription.cancellationDate) {
            warnAlert(t("subscription_not_canceled"));
            return;
        }

        // Pergunta antes porque desfazer uma reativação indevida significa cancelar de
        // novo, e o cancelamento encerra o contrato no provedor: o caminho de volta é bem
        // mais caro que o de ida.
        warnAlertYesOrNo(t(subscription.chargedAtStore
            ? "message.reactivation_store_package_confirmation"
            : "message.reactivation_confirmation"), () => {
            setShowLoading(true);
            axiosInstance.patch(`${Api.subscription.subscription}/${subscription.id}/reactivate`)
                .then(() => {
                    successAlert(t("message.transaction_successfully"), () => {
                        setShowDetailsModal(false);
                        executeSearch(page, size);
                    });
                })
                .catch((error) => ApiError(error))
                .finally(() => setShowLoading(false));
        });
    };

    // Abre a renovação do pacote da loja. Vale também para o pacote encerrado: renovar é o
    // caminho de volta dele, e o backend reabre o mesmo contrato.
    const openRenewPackage = (subscription) => {
        if (!subscription?.chargedAtStore) {
            return;
        }
        setShowDetailsModal(false);
        setRenewingSubscription(subscription);
    };

    // Depois de renovar, a lista recarrega para a validade e a situação novas aparecerem.
    const handlePackageRenewed = () => {
        setRenewingSubscription(null);
        executeSearch(page, size);
    };

    // Sincroniza manualmente o status da assinatura selecionada com a integração de pagamento.
    //
    // confirmCreate só vai preenchido na segunda tentativa. O backend procura no provedor o
    // contrato desta assinatura e o adota quando encontra; não encontrando nenhum, ele para e
    // responde 409 com o code subscription_creation_not_confirmed, porque criar um contrato
    // abre cobrança para o cliente e isso não pode ser efeito de um botão chamado Sincronizar.
    // Aí sim perguntamos, e repetimos a chamada com o aceite.
    const handleSyncStatus = (confirmCreate = false) => {
        if (!selectedSubscription) {
            warnAlert(t("validation.required.row"));
            return;
        }

        const url = `${Api.subscription.subscription}/${selectedSubscription.id}/sync`;

        setShowLoading(true);
        axiosInstance.post(confirmCreate ? `${url}?confirmCreate=true` : url)
            .then(() => {
                successAlert("Sincronização realizada com sucesso!", () => {
                    setShowDetailsModal(false);
                    executeSearch(page, size);
                });
            })
            .catch((error) => {
                const data = error?.response?.data;
                const needsCreationConfirmation = !confirmCreate
                    && data?.errors?.includes("subscription_creation_not_confirmed");

                if (needsCreationConfirmation) {
                    warnAlertYesOrNo(data.message, () => handleSyncStatus(true));
                    return;
                }

                errorAlert(error);
            })
            .finally(() => {
                setShowLoading(false);
            });
    };

    const formatMoney = (value) => `R$ ${convertDoubleToStringPTBR(Number(value || 0))}`;
    const formatPlanValue = (value) => `${t("subscription_plan_value")}: ${formatMoney(value)}`;
    const cancellationsPeriodLabel = startDate && endDate
        ? t("subscription_cancellations_period", {
            start: formatDate(startDate),
            end: formatDate(endDate)
        })
        : t("subscription_cancellations_all_period");

    // A carteira, o status de pagamento e os clientes perdidos são fotografias gerais;
    // apenas cancelamentos mede eventos no recorte selecionado. Pendências ficam fora da
    // carteira vigente porque ainda não representam um contrato ativo nem receita vigente.
    const summaryCards = [
        {
            key: "total",
            label: t("active_subscriptions"),
            value: summaryData?.totalSubscriptions ?? 0,
            valueLabel: formatPlanValue(summaryData?.totalValue),
            icon: "fa-solid fa-users",
            className: "subscriber-list__metric-card--total",
            actionLabel: t("subscription_view_in_list")
        },
        {
            key: "active",
            // "Em dia" = ativas SEM contar as atrasadas (ver card "overdue" logo abaixo,
            // que já mostra as atrasadas separadamente) - antes esse valor incluía as
            // atrasadas junto, o que fazia uma assinatura 100% em atraso aparecer como
            // "em dia".
            label: t("subscription_subscribers_on_time_label"),
            value: (summaryData?.activeSubscriptions ?? 0) - (summaryData?.overdueSubscriptions ?? 0),
            valueLabel: formatPlanValue((summaryData?.activeValue ?? 0) - (summaryData?.overdueValue ?? 0)),
            icon: "fa-solid fa-circle-check",
            className: "subscriber-list__metric-card--active",
            actionLabel: t("subscription_view_in_list")
        },
        {
            key: "overdue",
            label: t("overdue_subscriptions"),
            value: summaryData?.overdueSubscriptions ?? 0,
            valueLabel: formatPlanValue(summaryData?.overdueValue),
            icon: "fa-solid fa-triangle-exclamation",
            className: "subscriber-list__metric-card--overdue",
            actionLabel: t("subscription_view_in_list")
        },
        {
            key: "pending",
            label: t("awaiting_payment_subscriptions"),
            value: summaryData?.pendingSubscriptions ?? 0,
            valueLabel: formatPlanValue(summaryData?.pendingValue),
            icon: "fa-solid fa-hourglass-half",
            className: "subscriber-list__metric-card--pending",
            actionLabel: t("subscription_view_in_list")
        },
        {
            key: "inactive",
            label: t("subscription_lost_customers_global"),
            value: summaryData?.inactiveSubscriptions ?? 0,
            valueLabel: formatPlanValue(summaryData?.inactiveValue),
            icon: "fa-solid fa-circle-xmark",
            className: "subscriber-list__metric-card--inactive",
            // Aqui abrimos o histórico dos clientes perdidos, em vez de apenas
            // filtrar as assinaturas canceladas como no card de cancelamentos.
            onClick: openLostCustomers,
            actionLabel: t("subscription_lost_customers_action")
        },
        {
            key: "cancellationsInPeriod",
            label: cancellationsPeriodLabel,
            value: summaryData?.cancellationsInPeriod ?? 0,
            valueLabel: formatPlanValue(summaryData?.cancellationsInPeriodValue),
            icon: "fa-solid fa-calendar-xmark",
            className: "subscriber-list__metric-card--period",
            actionLabel: t("subscription_view_in_list")
        },
    ];

    // Selo e rótulo da situação vêm da regra comum (subscriptionSituation), a mesma do detalhe
    // da assinatura na venda, para as duas telas nunca discordarem.
    const getRowStatusTone = (row) => getSubscriptionSituation(row).tone;

    // Rótulo traduzido da situação, exibido na listagem e no modal.
    const getRowStatusLabel = (row) => t(getSubscriptionSituation(row).key);

    const getPlanMovementPresentation = (row) => {
        const movement = {
            UPGRADE: { symbol: "↑", label: "Upgrade", tone: "upgrade" },
            DOWNGRADE: { symbol: "↓", label: "Downgrade", tone: "downgrade" },
            PLAN_CHANGE: { symbol: "↔", label: "Troca de plano", tone: "change" }
        }[row?.planMovementType];

        if (!movement) {
            return null;
        }

        const previousPlan = row.previousPlanName || "Plano anterior não informado";
        const previousPrice = row.previousPlanPrice != null
            ? ` · ${formatMoney(row.previousPlanPrice)}`
            : "";
        const movementDate = row.planMovementDate
            ? ` · ${formatDate(row.planMovementDate)}`
            : "";

        return {
            ...movement,
            tooltip: `${movement.label}: ${previousPlan}${previousPrice} → ${row.plan?.name || "plano atual"}${movementDate}`
        };
    };

    // Converte o status da cobrança para a chave de tradução correspondente.
    const getChargeStatusLabel = (chargeStatus) => {
        const labelMap = {
            PENDING: "pending",
            PAID: "paid",
            OVERDUE: "overdue",
            CANCELED: "cancelled",
            CANCELLED: "cancelled",
            PROCESSING: "processing",
            REFUNDED: "refunded",
            FAILED: "failed"
        };

        return chargeStatus ? t(labelMap[chargeStatus] || chargeStatus.toLowerCase()) : "-";
    };

    const isSynced = (row) => !!row?.integrationId;

    const getSyncStatusLabel = (row) => {
        return isSynced(row) ? t("subscription_sync_synced") : t("subscription_sync_pending");
    };

    return (
        <>
            <ModalPageEvent
                title={t("subscription_details")}
                show={showDetailsModal}
                setShow={setShowDetailsModal}
                child={
                    selectedSubscription && (
                        <div className="sub-detail">
                            <section className="sub-detail__hero">
                                <div>
                                    <h3 className="sub-detail__name">{selectedSubscription.customer?.fullName || "-"}</h3>
                                    <p className="sub-detail__hero-text">
                                        {selectedSubscription.plan?.name || "-"}
                                        {/* Código do plano, o da primeira coluna da tela de Planos: distingue
                                            planos de mesmo nome (um desativado e um novo, por exemplo). */}
                                        {selectedSubscription.plan?.id ? ` (${t("plan_code_inline", { id: selectedSubscription.plan.id })})` : ""}
                                        {selectedSubscription.plan?.periodicity ? ` · ${t(selectedSubscription.plan.periodicity.toLowerCase())}` : ""}
                                        {/* Número da assinatura: o mesmo cliente pode ter mais de um contrato,
                                            inclusive no mesmo plano, e aí só o plano não distingue qual está aberto. */}
                                        {selectedSubscription.id ? ` · ${t("subscription_identification")} #${selectedSubscription.id}` : ""}
                                    </p>
                                </div>
                                <div className="sub-detail__hero-side">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        <span className={`subscription-workspace__status-badge subscription-workspace__status-badge--${getRowStatusTone(selectedSubscription)}`}>
                                            {getRowStatusLabel(selectedSubscription)}
                                        </span>
                                        {/* Pacote da loja não tem contrato no provedor para sincronizar;
                                            o que a recepção faz com ele é renovar. */}
                                        {selectedSubscription.chargedAtStore ? (
                                            <OverlayTrigger placement="bottom" overlay={<Tooltip>{t("store_package_renew")}</Tooltip>}>
                                                <span>
                                                    <button
                                                        className="btn sub-detail__hero-btn"
                                                        onClick={() => openRenewPackage(selectedSubscription)}
                                                        disabled={showLoading}
                                                        aria-label={t("store_package_renew")}
                                                    >
                                                        <i className="fa-solid fa-rotate-right"></i>
                                                    </button>
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                        <OverlayTrigger placement="bottom" overlay={
                                            <Tooltip>{isSynced(selectedSubscription) ? t("subscription_sync_status_action") : t("subscription_sync_create_action")}</Tooltip>
                                        }>
                                            <span>
                                                <button
                                                    className="btn sub-detail__hero-btn"
                                                    onClick={() => handleSyncStatus()}
                                                    disabled={showLoading || !!selectedSubscription?.cancellationDate}
                                                >
                                                    <i className="fa fa-refresh"></i>
                                                </button>
                                            </span>
                                        </OverlayTrigger>
                                        )}
                                        <OverlayTrigger placement="bottom" overlay={<Tooltip>Cancelar assinatura</Tooltip>}>
                                            <span>
                                                <button
                                                    className="btn sub-detail__hero-btn sub-detail__hero-btn--danger"
                                                    onClick={openCancelDialog}
                                                    disabled={!selectedSubscription || !!selectedSubscription.cancellationDate}
                                                >
                                                    <i className="fa fa-ban"></i>
                                                </button>
                                            </span>
                                        </OverlayTrigger>
                                    </div>
                                    <div className="sub-detail__hero-meta">
                                        <span>
                                            <i className="fa-regular fa-calendar me-2"></i>
                                            {t("hiring_date")}: {selectedSubscription.createdAt ? formatDateTime(selectedSubscription.createdAt) : "-"}
                                        </span>
                                    </div>
                                </div>
                            </section>

                            {/* Mesmos avisos do detalhe da assinatura na venda: plano sem serviços (nenhum
                                desconto) e plano desativado para vendas novas. */}
                            <PlanAttentionNotices plan={selectedSubscription.plan} />

                            <div className="sub-detail__grid">
                                {/* Nome, plano, periodicidade, status e data de contratação já aparecem
                                    no cabeçalho acima - não repetimos aqui para reduzir a informação
                                    duplicada e deixar o modal mais rápido de ler. */}
                                <section className="sub-detail__overview sub-detail__section--full">
                                    <div className="sub-detail__overview-group">
                                        <div className="sub-detail__overview-heading">
                                            <i className="fa-solid fa-user"></i>
                                            <span>Cliente</span>
                                        </div>
                                        <div className="sub-detail__overview-fields">
                                            <SubDetailItem label={t("phone")} value={selectedSubscription.customer?.phone} />
                                            <SubDetailItem label={t("email")} value={selectedSubscription.customer?.email} />
                                        </div>
                                    </div>
                                    <div className="sub-detail__overview-group">
                                        <div className="sub-detail__overview-heading">
                                            <i className="fa-solid fa-layer-group"></i>
                                            <span>Plano</span>
                                        </div>
                                        <div className="sub-detail__overview-fields">
                                            <SubDetailItem
                                                label={t("price")}
                                                value={`${t("currency_symbol")} ${selectedSubscription.plan?.price != null ? convertDoubleToStringPTBR(selectedSubscription.plan.price) : "0,00"}`}
                                                accent
                                            />
                                            {/* Na edição o item ocupa a linha inteira: espremido ao lado do
                                                preço, o seletor cortava o nome do colaborador. */}
                                            <div className={`sub-detail__item sub-detail__seller${editingSeller ? " sub-detail__seller--editing" : ""}`}>
                                                {/* A ação fica na linha do rótulo: ao lado do nome ela quebrava
                                                    de linha quando o nome era comprido, e o valor saía da altura
                                                    do preço ao lado. */}
                                                <div className="sub-detail__seller-head">
                                                    <span className="sub-detail__item-label">{t("subscription_sold_by")}</span>
                                                    {!editingSeller && (
                                                        <button type="button" className="sub-detail__seller-edit-btn" onClick={startSellerEdit}>
                                                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                                                            {selectedSubscription.employee?.id ? "Alterar" : "Definir"}
                                                        </button>
                                                    )}
                                                </div>
                                                {editingSeller ? (
                                                    <div className="sub-detail__seller-edit">
                                                        {/* Mesmo seletor do cadastro: vendedor pode ser qualquer
                                                            colaborador, mesmo quem não presta serviço. */}
                                                        <EmployeeSelect
                                                            value={sellerDraft}
                                                            onChange={setSellerDraft}
                                                            filterProvidesService={false}
                                                        />
                                                        <div className="sub-detail__seller-actions">
                                                            <Button size="sm" variant="primary" onClick={saveSeller} disabled={savingSeller}>
                                                                {t("save")}
                                                            </Button>
                                                            <Button size="sm" variant="outline-secondary" className="sub-detail__seller-cancel" onClick={() => setEditingSeller(false)} disabled={savingSeller}>
                                                                {t("cancel")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    // Sem vendedor, o texto diz isso em cinza em vez de um "-" em
                                                    // negrito, que parecia valor preenchido.
                                                    <strong className={`sub-detail__item-value${selectedSubscription.employee?.fullName ? "" : " sub-detail__item-value--empty"}`}>
                                                        {selectedSubscription.employee?.fullName || "Não informado"}
                                                    </strong>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <SubDetailSection
                                    icon="fa-solid fa-arrow-right-arrow-left"
                                    title="Histórico de planos"
                                    subtitle="Mudanças de plano registradas para este cliente"
                                    className="sub-detail__section--full"
                                    collapsible
                                >
                                    <div className="sub-detail__plan-history">
                                    {loadingPlanTransitions ? (
                                        <p className="sub-detail__notes">Carregando histórico...</p>
                                    ) : planTransitions.length > 0 ? (
                                            <div className="sub-detail__table-scroll">
                                                <table className="sub-detail__table">
                                                    <thead>
                                                        <tr><th>Tipo</th><th>Plano anterior</th><th>Novo plano</th><th>Data</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {planTransitions.map((transition) => (
                                                            <tr key={transition.id}>
                                                                <td><strong className={`sub-detail__plan-transition-type sub-detail__plan-transition-type--${transition.type?.toLowerCase()}`}>{transition.type === "UPGRADE" ? "↑ Upgrade" : transition.type === "DOWNGRADE" ? "↓ Downgrade" : "↔ Troca"}</strong></td>
                                                                <td>{transition.previousPlanName || "-"}</td>
                                                                <td>{transition.currentPlanName || "-"}</td>
                                                                <td>{transition.transitionDate ? formatDateTime(transition.transitionDate) : "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p className="sub-detail__notes">Nenhuma transição de plano registrada.</p>
                                        )}
                                    </div>
                                </SubDetailSection>

                                <SubDetailSection
                                    icon="fa-solid fa-clock-rotate-left"
                                    title="Últimas cobranças"
                                    subtitle={selectedSubscription.chargedAtStore
                                        ? "Os três pagamentos mais recentes do pacote, recebidos na loja"
                                        : "Os três ciclos mais recentes recebidos do provedor"}
                                    className="sub-detail__section--full"
                                    collapsible
                                >
                                    {selectedSubscription.customer?.paymentProviderCustomerId && (
                                        <div className="sub-detail__provider-customer-id">
                                            <div>
                                                <span>{t("payment_provider_customer_id")}</span>
                                                <strong>{selectedSubscription.customer.paymentProviderCustomerId}</strong>
                                            </div>
                                            <CopyButton
                                                textToCopy={selectedSubscription.customer.paymentProviderCustomerId}
                                                showText={false}
                                            />
                                        </div>
                                    )}
                                    {loadingRecentCharges ? (
                                        <p className="sub-detail__notes">Carregando cobranças...</p>
                                    ) : recentCharges.length > 0 ? (
                                        <div className="sub-detail__table-scroll">
                                            <table className="sub-detail__table sub-detail__table--charges">
                                                <thead>
                                                    <tr><th>Status</th><th>Valor</th><th>Vencimento</th><th>Pagamento</th><th>{t("actions")}</th></tr>
                                                </thead>
                                                <tbody>
                                                    {recentCharges.map((charge) => (
                                                        <tr key={charge.id}>
                                                            <td><span className={`sub-detail__charge-status sub-detail__charge-status--${charge.status?.toLowerCase()}`}>{getChargeStatusLabel(charge.status)}</span></td>
                                                            <td><strong>{formatMoney(charge.amount)}</strong></td>
                                                            <td>{charge.dueDate ? formatDate(charge.dueDate) : "-"}</td>
                                                            <td>{charge.paymentDate ? formatDateTime(charge.paymentDate) : "-"}</td>
                                                            {/* Cobrança já paga não precisa de link; manter o botão só
                                                                convidaria o cliente a pagar de novo. */}
                                                            <td>
                                                                {charge.status === "PAID" ? "-" : (
                                                                    <div className="sub-detail__charge-actions">
                                                                        <PaymentLinkActions
                                                                            charge={charge}
                                                                            phone={selectedSubscription.customer?.phone}
                                                                            compact
                                                                            onLinkGenerated={(paymentLink) => updateRecentChargeLink(charge.id, paymentLink)}
                                                                            // Depois de liberar, o botão some e o link já vale para PIX.
                                                                            onPixEnabled={(paymentLink) => updateRecentChargeLink(charge.id, paymentLink, "PIX")}
                                                                        />
                                                                        {/* Cliente que pagou no balcão: a baixa lança no caixa e
                                                                            avisa o provedor. Recarrega as cobranças para a linha
                                                                            passar a mostrar a data do pagamento. */}
                                                                        <OutsidePaymentAction
                                                                            charge={charge}
                                                                            branchId={selectedSubscription.customer?.branch?.id}
                                                                            compact
                                                                            onRegistered={() => loadRecentCharges(selectedSubscription)}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        /* Sem cobrança o operador ficava sem saída: os botões de link
                                           vivem dentro da tabela acima, então uma assinatura que nunca
                                           chegou ao provedor não oferecia nada. O caminho já existia no
                                           Sincronizar, mas ninguém procura o link com esse nome. */
                                        <div className="sub-detail__empty-charges">
                                            <p className="sub-detail__notes">Nenhuma cobrança encontrada para esta assinatura.</p>
                                            {!selectedSubscription.cancellationDate && !selectedSubscription.chargedAtStore
                                                && !isSynced(selectedSubscription) && (
                                                <>
                                                    <p className="sub-detail__empty-charges-hint">
                                                        {t("subscription_no_charge_link_hint")}
                                                    </p>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleSyncStatus()}
                                                    >
                                                        <i className="fas fa-link me-1"></i>
                                                        {t("generate_payment_link")}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </SubDetailSection>

                                {/* Cobrança: reúne forma de pagamento, sincronização e histórico de
                                    cobrança num único bloco (antes espalhados em duas seções). */}
                                <SubDetailSection
                                    icon="fa-solid fa-file-invoice-dollar"
                                    title={t("payment_status")}
                                    subtitle={selectedSubscription.chargedAtStore
                                        ? "Quem cobra, até quando o pacote vale e o último pagamento"
                                        : t("subscription_payment_status_hint")}
                                    className="sub-detail__section--full sub-detail__section--payment"
                                    collapsible
                                >
                                    {selectedSubscription.chargedAtStore ? (
                                    <div className="sub-detail__items-grid sub-detail__items-grid--3">
                                        <SubDetailItem label={t("subscription_billing_charged_by")} value={t("subscription_billing_store")} />
                                        <SubDetailItem label={t("store_package_valid_until")} value={selectedSubscription.storePackageValidUntil ? formatDate(selectedSubscription.storePackageValidUntil) : "-"} accent />
                                        <SubDetailItem label={t("payment_date")} value={selectedSubscription.lastChargePaymentDate ? formatDateTime(selectedSubscription.lastChargePaymentDate) : "-"} />
                                        <SubDetailItem label={t("cancellation_date")} value={selectedSubscription.cancellationDate ? formatDateTime(selectedSubscription.cancellationDate) : "-"} />
                                    </div>
                                    ) : (
                                    <div className="sub-detail__items-grid sub-detail__items-grid--3">
                                        <SubDetailItem label={t("payment_method")} value={getProviderBillingTypeLabel(selectedSubscription.providerBillingType)} />
                                        <SubDetailItem label={t("sync_status")} value={getSyncStatusLabel(selectedSubscription)} />
                                        {/* Contrato no provedor: sem ele o operador não tem como conferir, no painel
                                            do Asaas, qual das assinaturas do cliente é a que está sendo cobrada. */}
                                        <SubDetailItem label={t("provider_subscription_id")} value={selectedSubscription.integrationId} />
                                        <SubDetailItem label={t("last_due_date")} value={selectedSubscription.lastChargeDueDate ? formatDate(selectedSubscription.lastChargeDueDate) : "-"} />
                                        <SubDetailItem label={t("payment_date")} value={selectedSubscription.lastChargePaymentDate ? formatDateTime(selectedSubscription.lastChargePaymentDate) : "-"} />
                                        <SubDetailItem label={t("last_charge_status")} value={getChargeStatusLabel(selectedSubscription.lastChargeStatus)} />
                                        <SubDetailItem label={t("overdue_charges")} value={selectedSubscription.overdueChargeCount ?? 0} accent={(selectedSubscription.overdueChargeCount || 0) > 0} />
                                        <SubDetailItem label={t("cancellation_date")} value={selectedSubscription.cancellationDate ? formatDateTime(selectedSubscription.cancellationDate) : "-"} />
                                    </div>
                                    )}
                                </SubDetailSection>

                                {/* Observações só aparece quando há texto: um bloco vazio com "-" não
                                    ajuda o usuário e só ocupa espaço. */}
                                {selectedSubscription.obs && (
                                    <SubDetailSection
                                        icon="fa-solid fa-note-sticky"
                                        title={t("obs")}
                                        className="sub-detail__section--full"
                                        collapsible
                                    >
                                        <p className="sub-detail__notes">{selectedSubscription.obs}</p>
                                    </SubDetailSection>
                                )}
                            </div>
                        </div>
                    )
                }
                size="lg"
            />

            {/* Detalhe do card "Clientes perdidos": quem saiu, quando, por quê e como falar
                com a pessoa, que é o que transforma o número em ação. */}
            <ModalPageEvent
                title={t("subscription_lost_customers")}
                show={showLostCustomersModal}
                setShow={setShowLostCustomersModal}
                size="lg"
                dialogClassName="subscriber-lost-customers-modal"
                child={
                    <div className="sub-lost">
                        <div className="sub-lost__intro">
                            <span className="sub-lost__intro-icon" aria-hidden="true">
                                <i className="fa-solid fa-user-minus"></i>
                            </span>
                            <div className="sub-lost__intro-copy">
                                <p>{t("subscription_lost_customers_description")}</p>
                                {!loadingLostCustomers && (
                                    <span className="sub-lost__count">
                                        {t("subscription_lost_customers_count", { count: lostCustomers.length })}
                                    </span>
                                )}
                            </div>
                        </div>

                        {loadingLostCustomers ? (
                            <div className="sub-lost__state" role="status" aria-live="polite">
                                <span className="sub-lost__spinner" aria-hidden="true"></span>
                                <span>{t("loading")}...</span>
                            </div>
                        ) : lostCustomers.length === 0 ? (
                            <div className="sub-lost__state sub-lost__state--empty">
                                <span className="sub-lost__empty-icon" aria-hidden="true">
                                    <i className="fa-solid fa-circle-check"></i>
                                </span>
                                <strong>{t("subscription_lost_customers_empty_title")}</strong>
                                <p>{t("subscription_lost_customers_empty")}</p>
                            </div>
                        ) : (
                            <div className="sub-lost__list">
                                {lostCustomers.map((item) => {
                                    const customerInitials = (item.customerName || "?")
                                        .trim()
                                        .split(/\s+/)
                                        .slice(0, 2)
                                        .map((part) => part.charAt(0).toUpperCase())
                                        .join("");
                                    // Mantém a máscara para leitura, mas envia um telefone limpo ao discador.
                                    const phoneLink = item.customerPhone?.replace(/[^\d+]/g, "");

                                    return (
                                        <article key={item.subscriptionId} className="sub-lost__item">
                                            <div className="sub-lost__customer">
                                                <span className="sub-lost__avatar" aria-hidden="true">
                                                    {customerInitials}
                                                </span>
                                                <div className="sub-lost__identity">
                                                    <h3>{item.customerName || "-"}</h3>
                                                    {item.customerPhone ? (
                                                        <a className="sub-lost__phone" href={`tel:${phoneLink}`}>
                                                            <i className="fa-solid fa-phone" aria-hidden="true"></i>
                                                            {item.customerPhone}
                                                        </a>
                                                    ) : (
                                                        <span className="sub-lost__phone sub-lost__phone--missing">
                                                            <i className="fa-solid fa-phone-slash" aria-hidden="true"></i>
                                                            {t("phone_not_informed")}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="sub-lost__date">
                                                    <span>{t("cancellation_date")}</span>
                                                    <strong>{formatDate(item.cancellationDate)}</strong>
                                                </div>
                                            </div>

                                            <div className="sub-lost__details">
                                                <div className="sub-lost__plan">
                                                    <span className="sub-lost__detail-icon" aria-hidden="true">
                                                        <i className="fa-solid fa-crown"></i>
                                                    </span>
                                                    <div>
                                                        <span className="sub-lost__detail-label">{t("plan")}</span>
                                                        <strong>{item.planName || "-"}</strong>
                                                    </div>
                                                    {item.planValue != null && (
                                                        <span className="sub-lost__value">{formatMoney(item.planValue)}</span>
                                                    )}
                                                </div>
                                                {item.canceledBy && (
                                                    <div className="sub-lost__canceled-by">
                                                        <i className="fa-regular fa-user" aria-hidden="true"></i>
                                                        <span>{t("canceled_by")}: <strong>{item.canceledBy}</strong></span>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                }
            />

            <ModalPageEvent
                title={t("cancellation_subscribe")}
                show={showCancellationModal}
                setShow={setShowCancellationModal}
                dialogClassName="sub-cancel-modal"
                child={
                    <div className="sub-cancel">
                        <div className="sub-cancel__intro">
                            <span className="sub-cancel__intro-icon" aria-hidden="true">
                                <i className="fa-solid fa-ban"></i>
                            </span>
                            <div>
                                <strong>Encerrar esta assinatura?</strong>
                                <p>Confira os dados e as informações abaixo antes de confirmar.</p>
                            </div>
                        </div>
                        <div className="sub-cancel__warning">
                            <span className="sub-cancel__warning-icon">
                                <i className="fa-solid fa-triangle-exclamation"></i>
                            </span>
                            <div>
                                <strong>{t("attention")}</strong>
                                <p>{t("message.cancellation_confirmation")}</p>
                            </div>
                        </div>

                        {paidCycleUntil && (
                            <div className="sub-cancel__warning sub-cancel__warning--paid">
                                <span className="sub-cancel__warning-icon">
                                    <i className="fa-solid fa-circle-dollar-to-slot"></i>
                                </span>
                                <div>
                                    <strong>{t("paid_cycle_in_progress")}</strong>
                                    <p>{t(selectedSubscription?.chargedAtStore
                                        ? "message.cancellation_paid_package"
                                        : "message.cancellation_paid_cycle", { date: formatDate(paidCycleUntil) })}</p>
                                </div>
                            </div>
                        )}

                        {isLegacyEfiSubscription(selectedSubscription) && (
                            <label className="sub-cancel__legacy-choice">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={cancelLegacyEfiLocally}
                                    onChange={(event) => setCancelLegacyEfiLocally(event.target.checked)}
                                />
                                <span>
                                    <strong>Encerrar apenas no Colmeia</strong>
                                    <small>
                                        Esta assinatura é do EFI legado. Esta opção encerra o registro e os benefícios
                                        no Colmeia, mas não cancela cobranças ou contrato no EFI.
                                    </small>
                                </span>
                            </label>
                        )}

                        <div className="sub-cancel__card">
                            <h6 className="sub-cancel__card-title">
                                <i className="fa-solid fa-file-contract me-2"></i>
                                {t("subscribe_data")}
                            </h6>
                            <div className="sub-cancel__items">
                                <div className="sub-cancel__item">
                                    <span className="sub-cancel__item-label">{t("customer")}</span>
                                    <strong className="sub-cancel__item-value">{selectedSubscription?.customer?.fullName || "-"}</strong>
                                </div>
                                <div className="sub-cancel__item">
                                    <span className="sub-cancel__item-label">{t("phone")}</span>
                                    <strong className="sub-cancel__item-value">{selectedSubscription?.customer?.phone || "-"}</strong>
                                </div>
                                <div className="sub-cancel__item">
                                    <span className="sub-cancel__item-label">{t("plan")}</span>
                                    <strong className="sub-cancel__item-value">{selectedSubscription?.plan?.name || "-"}</strong>
                                </div>
                                <div className="sub-cancel__item">
                                    <span className="sub-cancel__item-label">{t("periodicity")}</span>
                                    <strong className="sub-cancel__item-value">
                                        {selectedSubscription?.plan?.periodicity ? t(selectedSubscription.plan.periodicity.toLowerCase()) : "-"}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        <label className="sub-cancel__ack">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={cancellationAcknowledged}
                                onChange={(e) => setCancellationAcknowledged(e.target.checked)}
                            />
                            {/* No pacote não existe cobrança recorrente para encerrar no provedor. */}
                            <span>{t(selectedSubscription?.chargedAtStore
                                ? "message.cancellation_acknowledgement_store_package"
                                : "message.cancellation_acknowledgement")}</span>
                        </label>
                    </div>
                }
                size="md"
                btnOnClick={handleCancel}
                btnText="Sim, confirmo"
                btnDisabled={!cancellationAcknowledged}
            />

            <PageTitle activeMenu={t("subscribers")} motherMenu={t("subscriptions")} />

            <FilterAccordion onSearch={handleSearch} onClear={handleClearFilters}>
                    <Row className="g-3">
                        <Col md={3}>
                            <FormLabel>
                                {dateFilterField === "cancellationDate"
                                    ? `${t("start_date")} (${t("cancellation_date")})`
                                    : t("start_date")}
                            </FormLabel>
                            <InputDate onChange={(value) => setStartDate(value)} selected={startDate} />
                        </Col>
                        <Col md={3}>
                            <FormLabel>
                                {dateFilterField === "cancellationDate"
                                    ? `${t("end_date")} (${t("cancellation_date")})`
                                    : t("end_date")}
                            </FormLabel>
                            <InputDate onChange={(value) => setEndDate(value)} selected={endDate} />
                        </Col>
                        <Col md={3}>
                            <FormLabel>{t("customer")}</FormLabel>
                            <InputText value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <FormLabel>{t("plan")}</FormLabel>
                            <InputText value={planName} onChange={(e) => setPlanName(e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <FormLabel>{t("subscription_sold_by")}</FormLabel>
                            <InputText
                                value={employeeName}
                                onChange={(e) => setEmployeeName(e.target.value)}
                                disabled={sellerInformed?.value === "N"}
                            />
                        </Col>
                        <Col md={3}>
                            <FormLabel>Vendedor informado</FormLabel>
                            {/* Com "Não", o nome do vendedor deixa de fazer sentido e fica travado. */}
                            <YesNoSelect
                                value={sellerInformed}
                                onChange={(value) => {
                                    setSellerInformed(value);
                                    if (value?.value === "N") setEmployeeName("");
                                }}
                                isClearable
                            />
                        </Col>
                        <Col md={3}>
                            <FormLabel>{t("active")}</FormLabel>
                            <YesNoSelect value={active} onChange={(value) => setActive(value)} isClearable isDisabled={!!planMovementType} />
                        </Col>
                        <Col md={3}>
                            <FormLabel>{t("late_payment")}</FormLabel>
                            <YesNoSelect value={hasOverdueCharge} onChange={(value) => setHasOverdueCharge(value)} isClearable />
                        </Col>
                        <Col md={3}>
                            <FormLabel>Movimentação de plano</FormLabel>
                            <CustomSelect
                                value={planMovementType}
                                options={planMovementOptions}
                                onChange={(option) => {
                                    setPlanMovementType(option);
                                    if (option) setActive({ value: "Y", label: t("yes") });
                                }}
                                placeholder="Carteira ativa"
                                isClearable
                            />
                        </Col>
                    </Row>
            </FilterAccordion>

            <div className="subscriber-list__metrics">
                {summaryCards.map((card) => {
                    const onClick = card.onClick || (() => applySummaryCardFilter(card.key));
                    const isSelected = appliedSummaryCard === card.key;

                    return (
                        <div
                            key={card.key}
                            className={`subscriber-list__metric-card ${card.className} is-clickable${isSelected ? ' is-selected' : ''}`}
                            onClick={onClick}
                            role="button"
                            tabIndex={0}
                            aria-pressed={card.onClick ? undefined : isSelected}
                            aria-haspopup={card.key === "inactive" ? "dialog" : undefined}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onClick();
                                }
                            }}
                        >
                            <div className="subscriber-list__metric-head">
                                <div>
                                    <span className="subscriber-list__metric-label">{card.label}</span>
                                </div>
                                <span className="subscriber-list__metric-icon">
                                    <i className={card.icon}></i>
                                </span>
                            </div>
                            {showLoading ? (
                                <div className="subscriber-list__metric-loading" role="status">
                                    <div className="subscriber-list__metric-skeleton" aria-hidden="true" />
                                    <div className="subscriber-list__metric-skeleton subscriber-list__metric-skeleton--caption" aria-hidden="true" />
                                    <span>{t("loading_indicators")}</span>
                                </div>
                            ) : (
                                <>
                                    <p className="subscriber-list__metric-value">{card.value}</p>
                                    {card.valueLabel && (
                                        <p className="subscriber-list__metric-money">{card.valueLabel}</p>
                                    )}
                                </>
                            )}
                            {card.subValue && (
                                <p className="subscriber-list__metric-subvalue">
                                    <i className="fa-solid fa-triangle-exclamation me-1"></i>
                                    {card.subValue}
                                </p>
                            )}
                            {card.actionLabel && !showLoading && (
                                <p className="subscriber-list__metric-action">
                                    {card.actionLabel} <i className="fa-solid fa-arrow-right ms-1"></i>
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            <Row className="mb-3 mt-2">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t("actions")}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => navigate(`${MenuConfig.subscription.subscription.route}/new`)}>
                                    <StarsIcon /> {t("subscribe_plan")}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={openDetailsModal}>
                                    <i className="fas fa-info-circle"></i> {t("view_details")}
                                </Dropdown.Item>
                                {selectedSubscription?.chargedAtStore && (
                                    <Dropdown.Item onClick={() => openRenewPackage(selectedSubscription)}>
                                        <i className="fa-solid fa-rotate-right"></i> {t("store_package_renew")}
                                    </Dropdown.Item>
                                )}
                                {selectedSubscription?.cancellationDate ? (
                                    <Dropdown.Item onClick={() => handleReactivate(selectedSubscription)}>
                                        <i className="fas fa-rotate-left"></i> {t("reactivate_subscribe")}
                                    </Dropdown.Item>
                                ) : (
                                    <Dropdown.Item onClick={openCancelDialog}>
                                        <CancelIcon /> {t("cancel_subscribe")}
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <div id="subscriber-list-results" className="subscriber-list__results">
                {appliedSummaryCard && (
                    <div className="subscriber-list__metric-filter" role="status">
                        <span>
                            {t("subscription_metrics_filter_applied", {
                                label: summaryCards.find((card) => card.key === appliedSummaryCard)?.label || ""
                            })}
                        </span>
                        <Button
                            type="button"
                            variant="link"
                            className="subscriber-list__metric-filter-clear"
                            onClick={handleClearFilters}
                        >
                            {t("subscription_metrics_filter_clear")}
                        </Button>
                    </div>
                )}

                    <GridCustomBase
                        pageable={pageable}
                        setPage={setPage}
                        setSize={setSize}
                        showLoading={showLoading}
                        selectedCount={selectedRows.length}
                        height={900}
                    >
                        <table className="table table-list i-table style-1 mb-0 border-0 subscriber-list__table">
                            <thead>
                                <tr>
                                    <th className="bg-none"></th>
                                    <th>{t("customer")}</th>
                                    <th>{t("plan")}</th>
                                    <th>{t("subscription_sold_by")}</th>
                                    <th>{t("status")}</th>
                                    <th>{t("hiring_date")}</th>
                                    <th>{t("cancellation_date")}</th>
                                    <th>{t("obs")}</th>
                                    <th>{t("actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const isSelected = selectedSubscription?.id === row.id;
                                    const isInactive = !!row.cancellationDate;
                                    const hasLatePayment = row.hasOverdueCharge && !isInactive;
                                    const planMovement = getPlanMovementPresentation(row);

                                    return (
                                        <tr
                                            key={row.id}
                                            className={isInactive
                                                ? "subscriber-list__row--inactive"
                                                : hasLatePayment
                                                    ? "subscriber-list__row--overdue"
                                                    : ""
                                            }
                                        >
                                            <td>
                                                <div className="form-check style-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`subscriber_${row.id}`}
                                                        checked={isSelected}
                                                        disabled={isInactive}
                                                        onChange={() => toggleRowSelection(row)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`subscriber_${row.id}`} />
                                                </div>
                                            </td>
                                            <td>
                                                <span className="subscription-workspace__cell-main">
                                                    {row.customer?.fullName || "-"}
                                                </span>
                                                <span className="subscription-workspace__cell-detail">
                                                    {row.customer?.phone || row.customer?.email || "-"}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="subscription-workspace__cell-main">
                                                    {row.plan?.name || "-"}
                                                </span>
                                                <span className="subscription-workspace__cell-detail">
                                                    {t("currency_symbol")} {row.plan?.price != null ? convertDoubleToStringPTBR(row.plan.price) : "0,00"}
                                                    {row.providerBillingType && !row.chargedAtStore ? ` · ${getProviderBillingTypeLabel(row.providerBillingType)}` : ""}
                                                </span>
                                            </td>
                                            <td>{row.employee?.fullName || "-"}</td>
                                            <td>
                                                <span className="subscriber-list__status-line">
                                                    <span className={`subscription-workspace__status-badge subscription-workspace__status-badge--${getRowStatusTone(row)}`}>
                                                        {getRowStatusLabel(row)}
                                                    </span>
                                                    {planMovement && (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{planMovement.tooltip}</Tooltip>}
                                                        >
                                                            <span
                                                                className={`subscriber-list__movement-badge subscriber-list__movement-badge--${planMovement.tone}`}
                                                                aria-label={planMovement.tooltip}
                                                            >
                                                                <span aria-hidden="true">{planMovement.symbol}</span>
                                                                <span className="subscriber-list__movement-label">{planMovement.label}</span>
                                                            </span>
                                                        </OverlayTrigger>
                                                    )}
                                                </span>
                                                {row.chargedAtStore && row.storePackageValidUntil ? (
                                                    <span className="subscription-workspace__cell-detail">
                                                        {t(row.cancellationDate ? "store_package_valid_until_past" : "store_package_valid_until_date",
                                                            { date: formatDate(row.storePackageValidUntil) })}
                                                    </span>
                                                ) : row.lastChargeDueDate && (
                                                    <span className="subscription-workspace__cell-detail">
                                                        {t("last_due_date")}: {formatDate(row.lastChargeDueDate)}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="subscription-workspace__cell-main">
                                                    {row.createdAt ? formatDateTime(row.createdAt) : "-"}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="subscription-workspace__cell-main">
                                                    {row.cancellationDate ? formatDateTime(row.cancellationDate) : "-"}
                                                </span>
                                            </td>
                                            <td>{row.obs || "-"}</td>
                                            <td>
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                        as={Button}
                                                        type="button"
                                                        variant="light"
                                                        size="sm"
                                                        className={`subscriber-list__detail-button subscriber-list__detail-toggle${
                                                            isInactive
                                                                ? " subscriber-list__detail-button--inactive"
                                                                : hasLatePayment
                                                                    ? " subscriber-list__detail-button--overdue"
                                                                    : ""
                                                        }`}
                                                    >
                                                        <i className="fas fa-ellipsis-vertical"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu as={RowActionsMenu}>
                                                        <Dropdown.Item onClick={() => openDetailsModalForRow(row)}>
                                                            <i className="fas fa-info-circle"></i> {t("view_details")}
                                                        </Dropdown.Item>
                                                        {row.chargedAtStore && (
                                                            <Dropdown.Item onClick={() => openRenewPackage(row)}>
                                                                <i className="fa-solid fa-rotate-right"></i> {t("store_package_renew")}
                                                            </Dropdown.Item>
                                                        )}
                                                        {/* Cancelar e reativar são a mesma decisão em dois sentidos:
                                                            mostrar as duas deixaria uma sempre cinza, e item
                                                            desabilitado não responde ao clique - o operador clica,
                                                            não acontece nada e abre chamado. */}
                                                        {isInactive ? (
                                                            <Dropdown.Item onClick={() => handleReactivate(row)}>
                                                                <i className="fas fa-rotate-left"></i> {t("reactivate_subscribe")}
                                                            </Dropdown.Item>
                                                        ) : (
                                                            <Dropdown.Item onClick={() => openCancelDialogForRow(row)}>
                                                                <CancelIcon /> {t("cancel_subscribe")}
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </GridCustomBase>
            </div>

            <StorePackageRenewModal
                subscription={renewingSubscription}
                onClose={() => setRenewingSubscription(null)}
                onRenewed={handlePackageRenewed}
            />
        </>
    );
};

export default SubscriberList;
